import { Appearance, Design } from '@Components/designConstants';
import '@Pages/GeoMMM/ScenarioPlanning/Modals/AdjustModal.scss';
import { MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md';
import { Modal } from 'react-bootstrap';
import * as R from 'ramda';
import { useMemo, useState, ReactElement } from 'react';
import { Button } from '@Components/Button';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { useSetError } from '../../../../redux/modals';

export const PLAN_OPTIONS = {
  KEEP_SPEND_AMOUNTS: 'Keep spend amounts',
  KEEP_PERCENT_CHANGES: 'Keep percent changes',
  START_FRESH: 'Start fresh'
} as const;
export type PLAN_OPTIONS = (typeof PLAN_OPTIONS)[keyof typeof PLAN_OPTIONS];

const ADJUST_PLAN_OPTIONS: { text: PLAN_OPTIONS; subtext: string }[] = [
  {
    text: PLAN_OPTIONS.KEEP_PERCENT_CHANGES,
    subtext: 'Reapply your % change values to the new BAU data.'
  },
  {
    text: PLAN_OPTIONS.KEEP_SPEND_AMOUNTS,
    subtext: 'Preserve your current spend values. Percent changes will adjust automatically.'
  },
  {
    text: PLAN_OPTIONS.START_FRESH,
    subtext: 'Remove all spend and flighting edits and begin with the new BAU data.'
  }
];

export interface AdjustModalProps {
  title: string;
  onApply: (option: PLAN_OPTIONS) => void;
  onHide: () => void;
  className?: string;
  infoText?: string;
  optionLabels?: Partial<Record<PLAN_OPTIONS, string>>;
  options?: { text: PLAN_OPTIONS; subtext: string }[];
}

export const AdjustModal = (props: AdjustModalProps): ReactElement => {
  const [selectedOption, setSelectedOption] = useState<PLAN_OPTIONS>();
  const {
    className = 'scenario-planning-adjust-modal',
    infoText = 'How should we handle your existing scenario plan edits?',
    onApply,
    onHide,
    optionLabels,
    options = ADJUST_PLAN_OPTIONS,
    title
  } = props;
  const setError = useSetError();
  const header = useMemo(() => <Modal.Header>{title}</Modal.Header>, [title]);
  const body = useMemo(
    () => (
      <Modal.Body>
        <div className='info'>{infoText}</div>
        <div className='options'>
          {options.map(option => (
            <InteractiveDiv
              key={option.text}
              className='option'
              onClick={() =>
                setSelectedOption(selectedOption === option.text ? undefined : option.text)
              }
            >
              <div className='select-icon'>
                {option.text === selectedOption ? <MdCheckBox /> : <MdCheckBoxOutlineBlank />}
              </div>
              <div className='text-container'>
                <div className='text'>{optionLabels?.[option.text] ?? option.text}</div>
                <div className='subtext'>{option.subtext}</div>
              </div>
            </InteractiveDiv>
          ))}
        </div>
      </Modal.Body>
    ),
    [infoText, optionLabels, options, selectedOption]
  );
  const footer = useMemo(
    () => (
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={() => onHide()}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (R.isNil(selectedOption)) {
              setError({
                message: 'Please select an option.'
              });
            } else {
              onApply(selectedOption);
            }
          }}
        >
          Apply
        </Button>
      </Modal.Footer>
    ),
    [onApply, onHide, selectedOption, setError]
  );
  return (
    <Modal show onHide={onHide} centered className={className} size='lg'>
      {header}
      {body}
      {footer}
    </Modal>
  );
};
